import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Card from 'components/atoms/Card/Card';
import Currency from 'components/atoms/Currency/Currency';
import Txt from 'components/atoms/Txt/Txt';
import { useNamespace } from 'components/templates/Translation/TranslationWrapper';
import SurveyIcon from 'components/molecules/SurveyCard/SurveyIcon.qmee';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import ClockIcon from './assets/clock.svg';
import IconPii from 'components/atoms/Tag/assets/pii.svg';
import IconCamera from './assets/survey_icons/survey_icon_camera.svg';
import IconHotDeal from './assets/survey_icons/survey_icon_hotdeal.svg';
import Chevron from './assets/Chevron';
import classes from './SurveyCard.qmee.module.scss';

export type Props = {
  surveyId: string;
  surveyUrl?: string;
  title?: string;
  duration: number;
  amount: number;
  baseAmount?: number;
  disabled?: boolean;
  onSurveyClick?: () => void;
  className?: string;
  tags?: ReadonlyArray<string>;
  currency?: string;
  isOnGoingSurvey?: boolean;
};

export default function SurveyCardStatic(props: Props) {
  const namespace = useNamespace();
  const { t } = useTranslation([namespace, 'earn-money-online']);
  const titleKey = props.title || 'publicSurveys.default.title';
  const isStaticSurveyTitleKey = titleKey.startsWith('publicSurveys.');
  const displayTitle = t(
    titleKey,
    isStaticSurveyTitleKey ? { ns: 'earn-money-online' } : undefined,
  );

  const Inner = useCallback(
    ({ surveyId, duration, amount, disabled, tags, currency, isOnGoingSurvey }: Props) => (
      <div
        className={clsx(
          classes.root,
          disabled ? classes.disabled : '',
          !disabled && classes.hoverEffects,
        )}
      >
        <div className={classes.link}>
          <div className={classes.iconDiv}>
            <SurveyIcon
              surveyTitle={props.title}
              surveyTags={tags ?? []}
              surveyId={props.surveyId}
            />
          </div>
          <div className={classes.test}>
            <div className={classes.supportingInfo}>
              <span>
                <Txt skipTranslation>{displayTitle}</Txt>
              </span>

              <span>
                {tags && tags.includes('webcam') && <IconCamera className={classes.exampleIcon} />}
                {tags && tags.includes('hot') && <IconHotDeal className={classes.exampleIcon} />}
                {tags && tags.includes('collects_pii') && (
                  <IconPii className={classes.exampleIcon} />
                )}
              </span>
            </div>
            <div className={classes.primaryContent}>
              {props.baseAmount != null ? (
                <div className={classes.rewardWrapper}>
                  <Currency
                    amount={props.baseAmount}
                    currency={currency || 'USD'}
                    className={classes.basePoints}
                    numberClassName={classes.baseAmount}
                    currencyClassName={classes.baseCurrencySymbol}
                  />
                  <Currency
                    amount={amount}
                    currency={currency || 'USD'}
                    className={classes.points}
                    numberClassName={classes.amount}
                    currencyClassName={classes.currencySymbol}
                  />
                </div>
              ) : (
                <Currency
                  amount={amount}
                  currency={currency || 'USD'}
                  className={classes.points}
                  numberClassName={classes.amount}
                  currencyClassName={classes.currencySymbol}
                />
              )}
              {isOnGoingSurvey ? (
                <Txt className={classes.continue}>
                  <span>Continue</span>
                  <Chevron className={classes.chevron} />
                </Txt>
              ) : (
                <Txt className={classes.duration}>
                  <ClockIcon className={classes.clockIcon} />
                  <Txt className={classes.minutes} skipTranslation>
                    {{ duration: duration }}
                  </Txt>{' '}
                  <span className={classes.units}>mins</span>
                </Txt>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
    [displayTitle, props.baseAmount, props.surveyId, props.title],
  );

  return (
    <Card className={clsx(classes.root, props.className)} isLink>
      {props.surveyUrl && !props.disabled ? (
        <A href={props.surveyUrl} className={classes.link} onClick={props.onSurveyClick}>
          <Inner {...props} />
        </A>
      ) : (
        <div className={classes.link}>
          <Inner {...props} />
        </div>
      )}
    </Card>
  );
}
